import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import ConfigProvider from "antd/es/config-provider";
import notification from "antd/es/notification";
import { preMessage } from "rc-util/es/warning";
import { RawIntlProvider } from "react-intl";
import { Route, Switch, useHistory } from "react-router-dom";

import { OrganizationSetting, ShortcutService } from "@mapmycustomers/shared";
import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import Currency from "@mapmycustomers/shared/types/Currency";
import { GuideUserStatus } from "@mapmycustomers/shared/types/entity";
import {
  ConfigProviderProps,
  ModalsProvider,
  PrimitiveType,
  ConfigProvider as UiConfigProvider,
} from "@mapmycustomers/ui";

import AuthRoute from "@app/component/auth/AuthRoute";
import PrivateRoute from "@app/component/auth/PrivateRoute";
import PublicRoute from "@app/component/auth/PublicRoute";
import SsoLayout from "@app/component/Layout/SsoLayout";
import configService from "@app/config/ConfigService";
import dateFnsLocaleService from "@app/config/DateFnsLocaleService";
import i18nService from "@app/config/I18nService";
import Environment from "@app/enum/Environment";
import Path from "@app/enum/Path";
import {
  ActivityPage,
  BillingPage,
  CompanyPage,
  DashboardPage,
  DealPage,
  EmailOutboxPage,
  EmailUnsubscribePage,
  FilesPage,
  MapPage,
  NotificationsPage,
  OnboardingPage,
  PeoplePage,
  ReportsPage,
  ResourcesPage,
  RoutePage,
  SettingsPage,
  Web1Page,
} from "@app/scene";
import AuthCallbackPage from "@app/scene/auth/component/AuthCallback";
import ForgotPasswordPage from "@app/scene/auth/component/ForgotPassword";
import ResetPasswordPage from "@app/scene/auth/component/ForgotPassword/ResetPasswordPage";
import LoginPage from "@app/scene/auth/component/Login";
import ManageUserAccess from "@app/scene/auth/component/ManageUserAccess";
import RegisterPage from "@app/scene/auth/component/Register";
import SsoLoginPage from "@app/scene/auth/component/SsoLogin";
import RootPage from "@app/scene/auth/RootPage";
import { isSignedIn } from "@app/store/auth";
import { showGlobalSearch } from "@app/store/globalSearch/actions";
import {
  getCurrentUserId,
  getIntegrationNameIfIntegrationOnlyGetter,
  getOrganizationSettingValue,
} from "@app/store/iam";
import { getLocale, isLoading } from "@app/store/locale";
import { getGuidesStatus } from "@app/store/onboarding";
import { updateGuideStatus } from "@app/store/onboarding/actions";
import { getCurrencies } from "@app/store/referenceData";
import { RootState } from "@app/store/rootReducer";
import { isAnyThemeLoaded } from "@app/store/theme";
import {
  getUserColorClassNameGetter,
  UserColorClassNameGetter,
} from "@app/store/userColor/selectors";
import Iam from "@app/types/Iam";
import analyticsService from "@app/util/analytic/AnalyticsService";
import { DEFAULT_NOTIFICATION_DURATION } from "@app/util/consts";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import defaultEntityLinkGetter from "@app/util/contexts/defaultEntityLinkGetter";
import EntityLinkGetterContext from "@app/util/contexts/EntityLinkGetterContext";
import GuidesContext from "@app/util/contexts/GuidesContext";
import useGuidesService from "@app/util/contexts/useGuidesService";
import { default as formatCountryNameFromCode } from "@app/util/countries/formatCountryName";
import { complementFileUrl } from "@app/util/file";
import useFindMyLocation from "@app/util/hook/useFindMyLocation";
import { getFieldCategoryDisplayName } from "@app/util/ui";

import messages from "./messages";

// lower down antd deprecation messages from error to warning
preMessage((message, type) => {
  if (type === "warning" && message) {
    console.warn(message);
    return null;
  } else {
    return message;
  }
});

interface AppProps {
  currencies: Currency[];
  defaultCurrencyCode: Currency["id"];
  getIntegrationNameIfIntegrationOnly?: ConfigProviderProps["getIntegrationNameIfIntegrationOnly"];
  getUserColorClassName: UserColorClassNameGetter;
  isAnyThemeLoaded: boolean;
  isSignedIn: boolean | undefined;
  locale: string;
  // localeLoading is needed to be in props to guarantee App component is re-rendered when locale
  // initialization is complete. `locale` prop is not enough because sometimes it has the same
  // value before and after initialization. This is needed to get correct intl instance from
  // the i18nService.
  localeLoading: boolean;
  me?: Iam;
  myGuideStatuses: GuideUserStatus[];
  onUpdateGuideStatus: typeof updateGuideStatus.request;
  showGlobalSearch: typeof showGlobalSearch;
}

notification.config({
  duration: DEFAULT_NOTIFICATION_DURATION,
});

const App = ({
  currencies,
  defaultCurrencyCode,
  getIntegrationNameIfIntegrationOnly,
  getUserColorClassName,
  isAnyThemeLoaded,
  isSignedIn,
  locale,
  myGuideStatuses,
  onUpdateGuideStatus,
  showGlobalSearch,
}: AppProps) => {
  const antDLocale = i18nService.getAntDLocale(locale);
  const intl = i18nService.getIntl();

  const history = useHistory();
  useEffect(() => history.listen(() => analyticsService.page()), [history]);

  useEffect(() => {
    const shortcutService = new ShortcutService(document.body);
    shortcutService.addKeyListener(
      { code: "KeyF", metaKey: true, shiftKey: true, type: "keydown" },
      () => showGlobalSearch()
    );
    return () => shortcutService.destroy();
  }, [showGlobalSearch]);

  const formatMessage = useCallback(
    (messageId: keyof typeof messages, values?: Record<string, PrimitiveType>) =>
      messageId in messages ? intl?.formatMessage(messages[messageId], values) : undefined,
    [intl]
  );

  const formatCountryName = useCallback(
    (countryCode: CountryCode) => {
      return intl ? formatCountryNameFromCode(intl, countryCode) : "";
    },
    [intl]
  );

  const formatFieldCategoryDisplayName = useCallback(
    (fieldCategory: FieldCategory) => {
      return intl ? getFieldCategoryDisplayName(intl, fieldCategory) : fieldCategory;
    },
    [intl]
  );

  const guidesContext = useGuidesService(myGuideStatuses, onUpdateGuideStatus, isSignedIn);

  if (!intl || !isAnyThemeLoaded) {
    return null;
  }

  return (
    <RawIntlProvider value={intl}>
      <ConfigProvider locale={antDLocale}>
        <UiConfigProvider
          complementFileUrl={complementFileUrl}
          currencies={currencies}
          dateFnsLocale={dateFnsLocaleService.locale}
          defaultCurrencyCode={defaultCurrencyCode}
          formatCountryName={formatCountryName}
          formatMessage={formatMessage as ConfigProviderProps["formatMessage"]}
          getFieldCategoryDisplayName={formatFieldCategoryDisplayName}
          getIntegrationNameIfIntegrationOnly={getIntegrationNameIfIntegrationOnly}
          getUserColorClassName={getUserColorClassName}
          intl={intl}
          useFindMyLocation={useFindMyLocation}
        >
          <AnalyticsContext.Provider value={analyticsService}>
            <ModalsProvider>
              <EntityLinkGetterContext.Provider value={defaultEntityLinkGetter}>
                <GuidesContext.Provider value={guidesContext}>
                  <Switch>
                    <AuthRoute path={Path.LOGIN} component={LoginPage} />
                    <AuthRoute path={Path.FORGOT_PASSWORD} component={ForgotPasswordPage} />
                    <AuthRoute path={Path.RESET_PASSWORD} component={ResetPasswordPage} />
                    <AuthRoute path={Path.SSO_LOGIN} component={SsoLoginPage} />
                    <AuthRoute path={Path.REGISTER} component={RegisterPage} />
                    <PrivateRoute
                      path={Path.MANAGE_USER_ACCESS}
                      component={ManageUserAccess}
                      layoutComponent={SsoLayout}
                    />
                    <PrivateRoute path={Path.DASHBOARD} component={DashboardPage} />
                    <PrivateRoute path={Path.ONBOARDING} component={OnboardingPage} />
                    <PrivateRoute path={Path.ACTIVITY} component={ActivityPage} />
                    {configService.getCurrentEnvironment() !== Environment.SANDBOX && (
                      <PrivateRoute path={Path.BILLING} component={BillingPage} />
                    )}
                    <PrivateRoute path={Path.COMPANY} component={CompanyPage} />
                    <PrivateRoute path={Path.EMAIL_OUTBOX} component={EmailOutboxPage} />
                    <PrivateRoute path={`${Path.FILES}/:fileId`} component={FilesPage} />
                    <PrivateRoute path={Path.MAP} component={MapPage} />
                    <PrivateRoute path={Path.PEOPLE} component={PeoplePage} />
                    <PrivateRoute path={Path.REPORTS} component={ReportsPage} />
                    <PrivateRoute path={Path.RESOURCES} component={ResourcesPage} />
                    <PrivateRoute path={Path.ROUTE} component={RoutePage} />
                    <PrivateRoute path={Path.SETTINGS} component={SettingsPage} />
                    <PrivateRoute path={Path.DEAL} component={DealPage} />
                    <PrivateRoute
                      path={`${Path.NOTIFICATIONS}/:notificationId?`}
                      component={NotificationsPage}
                    />
                    <PrivateRoute layout={false} path={Path.WEB_1} component={Web1Page} />
                    <PublicRoute
                      component={EmailUnsubscribePage}
                      path={`${Path.EMAIL_UNSUBSCRIBE}`}
                    />
                    <PublicRoute component={AuthCallbackPage} path={Path.AUTH_CALLBACK} />
                    {/* Must be the last one */}
                    <Route path={Path.ROOT} component={RootPage} />
                  </Switch>
                </GuidesContext.Provider>
              </EntityLinkGetterContext.Provider>
            </ModalsProvider>
          </AnalyticsContext.Provider>
        </UiConfigProvider>
      </ConfigProvider>
    </RawIntlProvider>
  );
};

const mapStateToProps = (state: RootState) => {
  const currentUserId = getCurrentUserId(state);
  return {
    currencies: getCurrencies(state),
    defaultCurrencyCode: getOrganizationSettingValue(state, OrganizationSetting.CURRENCY),
    getIntegrationNameIfIntegrationOnly: getIntegrationNameIfIntegrationOnlyGetter(state),
    getUserColorClassName: getUserColorClassNameGetter(state),
    isAnyThemeLoaded: isAnyThemeLoaded(state),
    isSignedIn: isSignedIn(state),
    locale: getLocale(state),
    localeLoading: isLoading(state),
    myGuideStatuses: getGuidesStatus(state).filter(({ user }) => user.id === currentUserId),
  };
};

const mapDispatchToProps = {
  onUpdateGuideStatus: updateGuideStatus.request,
  showGlobalSearch,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
